<template>
  <div :class="[$style.range, column && $style['range--column']]">
    <DolphinSelector
      :class="$style.range__selector"
      v-model="minValue"
      :options="minOptions"
      :disabled="disabled"
      :placeholder="minPlaceholder"
    />
    <span :class="$style.range__divider" v-if="!column && !isHideTilde"
      >〜</span
    >
    <DolphinSelector
      :class="$style.range__selector"
      v-model="maxValue"
      :options="maxOptions"
      :disabled="disabled"
      :placeholder="maxPlaceholder"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import type { Option, OptionValueType, RangeType } from '@/types/app'

const props = withDefaults(
  defineProps<{
    modelValue?: RangeType
    minOptions?: Option<OptionValueType>[]
    maxOptions?: Option<OptionValueType>[]
    minPlaceholder?: string
    maxPlaceholder?: string
    disabled?: boolean
    column?: boolean
    isHideTilde?: boolean
  }>(),
  {
    modelValue: () => ({
      min: null,
      max: null,
    }),
    minOptions: () => [],
    maxOptions: () => [],
    minPlaceholder: '',
    maxPlaceholder: '',
    disabled: false,
    column: false,
    isHideTilde: false,
  }
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: RangeType): void
}>()

const isNullOrUndefined = (value: unknown): value is null | undefined =>
  value === null || value === undefined

const minValue = computed({
  get(): RangeType['min'] {
    return props.modelValue.min
  },
  set(value: RangeType['min']): void {
    if (
      isNullOrUndefined(value) ||
      isNullOrUndefined(props.modelValue.max) ||
      (typeof props.modelValue.max === 'number' &&
        props.modelValue.max >= value)
    ) {
      emit('update:modelValue', {
        min: value,
        max: props.modelValue.max,
      })
      return
    }
    const availableMaxValues = props.maxOptions
      .filter(
        option => option.value != undefined && (option.value as number) >= value
      )
      .map<number>(option => option.value as number)
    const availableMaxValue = Math.min(...availableMaxValues)
    emit('update:modelValue', {
      min: value,
      max: availableMaxValue,
    })
  },
})

const maxValue = computed({
  get(): RangeType['max'] {
    return props.modelValue.max
  },
  set(value: RangeType['max']): void {
    if (
      isNullOrUndefined(value) ||
      isNullOrUndefined(props.modelValue.min) ||
      (typeof props.modelValue.min === 'number' &&
        props.modelValue.min <= value)
    ) {
      emit('update:modelValue', {
        min: props.modelValue.min,
        max: value,
      })
      return
    }
    const availableMinValues = props.minOptions
      .filter(
        option => option.value != undefined && (option.value as number) <= value
      )
      .map<number>(option => option.value as number)
    const availableMaxValue = Math.max(...availableMinValues)
    emit('update:modelValue', {
      min: availableMaxValue,
      max: value,
    })
  },
})
</script>

<style lang="scss" module>
.range {
  $root: &;

  display: flex;
  align-items: center;
  gap: 8px;

  &--column {
    flex-direction: column;
    align-items: stretch;
  }

  &__divider {
    flex: 0 0 auto;
  }

  &__selector {
    min-height: 48px;
    flex: 1;
  }
}
</style>
